import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { lazy, Suspense } from "react";

const ShareButtons = lazy(() =>
  import("@/components/ShareButtons").then((m) => ({ default: m.ShareButtons })),
);
import { ideaBySlug, ideas } from "@/content/en/ideas";
import type { Idea } from "@/content/en/types";
import { conceptBySlug } from "@/content/en/concepts";
import { researchBySlug } from "@/content/en/research";
import { CitationBox, BriefingCTA, TopicList } from "@/components/en/EnPrimitives";
import { MachineSummary } from "@/components/en/MachineSummary";
import {
  personSchema,
  breadcrumbSchema,
  enHreflangLinks,
  PERSON_ID,
  WEBSITE_ID,
  EN_HOME,
  SITE_URL,
  TR_SITE_URL,
} from "@/lib/en-schema";
import { truncate } from "@/lib/en-meta";
import { pageTitle } from "@/lib/brand";


export const Route = createFileRoute("/ideas/$slug")({
  loader: ({ params }) => {
    const idea = ideaBySlug(params.slug);
    if (!idea) throw notFound();
    return { idea };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: pageTitle("Idea not found", "en") },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const i = loaderData.idea;
    const canonical = `https://smartsustainability.ai/ideas/${params.slug}`;
    return {
      meta: [
        { title: pageTitle(`${i.title}`, "en") },
        { name: "description", content: truncate(i.abstract) },
        { property: "og:title", content: i.title },
        { property: "og:description", content: truncate(i.abstract) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: canonical },
        { property: "og:locale", content: "en_US" },
        // Only claim a Turkish alternate when a real counterpart exists.
        ...(i.turkishHref ? [{ property: "og:locale:alternate", content: "tr_TR" }] : []),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: i.title },
      { name: "twitter:description", content: truncate(i.abstract) },
        { property: "article:published_time", content: `${i.published}T00:00:00Z` },
        { property: "article:modified_time", content: `${i.updated}T00:00:00Z` },
        ...i.topics.map((t) => ({ property: "article:tag", content: t })),
      ],
      links: [
        { rel: "canonical", href: canonical },
        ...enHreflangLinks(
          canonical,
          i.turkishHref ? `${TR_SITE_URL}${i.turkishHref}` : undefined,
        ),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(personSchema()),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: i.title,
            description: i.abstract,
            author: { "@id": PERSON_ID },
            datePublished: i.published,
            dateModified: i.updated,
            inLanguage: "en",
            articleSection: i.topics[0] ?? "Ideas",
            keywords: i.topics.join(", "),
            isPartOf: { "@id": WEBSITE_ID },
            publisher: { "@id": PERSON_ID },
            mainEntityOfPage: canonical,
            url: canonical,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbSchema([
              { name: "Home", item: EN_HOME },
              { name: "Ideas", item: `${SITE_URL}/ideas` },
              { name: i.title, item: canonical },
            ]),
          ),
        },
      ],
    };
  },

  component: IdeaDetail,
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-6 py-20 text-center">
      <p className="text-[10px] uppercase tracking-[0.22em] text-clay">Ideas</p>
      <h1 className="mt-4 font-serif text-3xl text-ink">Idea not found</h1>
      <Link to="/ideas" className="mt-6 inline-block text-[11px] uppercase tracking-[0.2em] text-clay underline underline-offset-4">
        Back to Ideas
      </Link>
    </div>
  ),
});

function IdeaDetail() {
  const { idea: i } = Route.useLoaderData() as { idea: Idea };
  const related = ideas.filter((x) => x.slug !== i.slug).slice(0, 2);
  return (
    <article className="mx-auto max-w-3xl px-6 py-16 md:py-20">
      {/* Header */}
      <header className="mb-12">
        <p className="text-[10px] uppercase tracking-[0.24em] text-clay">Idea</p>
        <h1 className="mt-4 font-serif text-4xl leading-tight text-ink md:text-5xl">
          {i.title}
        </h1>
        <p className="mt-4 font-serif italic text-lg text-clay md:text-xl">
          {i.subtitle}
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
          <span>Arda Öztaşkın</span>
          <span aria-hidden>·</span>
          <time dateTime={i.published}>{formatDate(i.published)}</time>
          <span aria-hidden>·</span>
          <span>{i.readingMinutes} min read</span>
          <span aria-hidden>·</span>
          <span>Last updated {formatDate(i.updated)}</span>
        </div>
        <Suspense fallback={null}>
          <ShareButtons
            path={`/ideas/${i.slug}`}
            title={i.title}
            excerpt={i.subtitle}
            category="Idea"
            origin="https://smartsustainability.ai"
            lang="en"
            size="md"
            className="mt-6"
          />
        </Suspense>
      </header>

      {/* Machine-readable summary — visible to readers and AI systems */}
      <MachineSummary
        kind="idea"
        slug={i.slug}
        summary={i.subtitle}
        coreQuestion={
          i.title.trim().endsWith("?")
            ? i.title
            : `How should we read: ${i.title}?`
        }
        coreClaim={i.fiveSecondAnswer}
        relatedConceptSlugs={i.relatedConcepts}
        relatedIdeaSlugs={ideas
          .filter((x) => x.slug !== i.slug && x.topics.some((t) => i.topics.includes(t)))
          .slice(0, 3)
          .map((x) => x.slug)}
        topics={i.topics}
        updated={i.updated}
      />



      {/* Abstract */}
      <section className="mb-10 rounded-md border-l-4 border-ink/20 pl-5">
        <p className="text-[10px] uppercase tracking-[0.22em] text-clay mb-2">Abstract</p>
        <p className="text-[16px] leading-relaxed text-foreground/85">{i.abstract}</p>
      </section>

      {/* 5-second answer */}
      <section className="mb-10">
        <h2 className="text-[10px] uppercase tracking-[0.22em] text-clay">5-Second Answer</h2>
        <p className="mt-3 font-serif text-xl leading-snug text-ink md:text-2xl">
          {i.fiveSecondAnswer}
        </p>
      </section>

      {/* Key arguments */}
      <section className="mb-10">
        <h2 className="mb-4 font-serif text-2xl text-ink">Key Arguments</h2>
        <ol className="space-y-3 list-decimal list-outside pl-6 text-[15px] leading-relaxed text-foreground/85">
          {i.keyArguments.map((a, idx) => (
            <li key={idx}>{a}</li>
          ))}
        </ol>
      </section>

      {/* Analysis */}
      <section className="mb-10">
        <h2 className="mb-4 font-serif text-2xl text-ink">Analysis</h2>
        <div className="space-y-5 text-[16px] leading-[1.7] text-foreground/85">
          {i.analysis.split("\n\n").map((p, idx) => (
            <p key={idx}>{p}</p>
          ))}
        </div>
      </section>

      {/* Counterarguments */}
      <section className="mb-10">
        <h2 className="mb-4 font-serif text-2xl text-ink">Counterarguments</h2>
        <p className="text-[16px] leading-[1.7] text-foreground/85">{i.counterarguments}</p>
      </section>

      {/* Implications */}
      <section className="mb-12">
        <h2 className="mb-4 font-serif text-2xl text-ink">Implications</h2>
        <p className="text-[16px] leading-[1.7] text-foreground/85">{i.implications}</p>
      </section>

      {/* Topics */}
      <section className="mb-12">
        <TopicList topics={i.topics} />
      </section>

      {/* Related concepts */}
      {i.relatedConcepts.length > 0 && (
        <section className="mb-10 rounded-md border border-ink/10 bg-paper p-6">
          <h2 className="mb-4 font-serif text-lg text-ink">Related concepts</h2>
          <ul className="space-y-3">
            {i.relatedConcepts.map((slug) => {
              const c = conceptBySlug(slug);
              if (!c) return null;
              return (
                <li key={slug}>
                  <Link
                    to="/concepts/$slug"
                    params={{ slug }}
                    className="group block"
                  >
                    <p className="font-serif text-base text-ink group-hover:text-clay">
                      {c.name}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">{c.oneLine}</p>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* Related research */}
      {i.relatedResearch.length > 0 && (
        <section className="mb-10 rounded-md border border-ink/10 bg-paper p-6">
          <h2 className="mb-4 font-serif text-lg text-ink">Related research</h2>
          <ul className="space-y-3">
            {i.relatedResearch.map((slug) => {
              const r = researchBySlug(slug);
              if (!r) return null;
              return (
                <li key={slug}>
                  <Link
                    to="/research/$slug"
                    params={{ slug }}
                    className="group block"
                  >
                    <p className="font-serif text-base text-ink group-hover:text-clay">
                      {r.title}
                    </p>
                    <p className="mt-1 text-xs uppercase tracking-[0.14em] text-muted-foreground">
                      {r.institution} · {r.year}
                    </p>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* References */}
      {i.references.length > 0 && (
        <section className="mb-10">
          <h2 className="mb-4 font-serif text-xl text-ink">References</h2>
          <ul className="space-y-2 text-sm text-foreground/80">
            {i.references.map((r, idx) => (
              <li key={idx}>
                {r.url ? (
                  <a href={r.url} target="_blank" rel="noopener" className="underline underline-offset-2 hover:text-clay">
                    {r.label}
                  </a>
                ) : (
                  <span>{r.label}</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <CitationBox year={new Date(i.published).getFullYear()} title={i.title} path={`/ideas/${i.slug}`} />

      {/* TR link if exists */}
      {i.turkishHref && (
        <p className="mb-10 text-sm text-muted-foreground">
          <a href={i.turkishHref} className="underline underline-offset-4 hover:text-clay">
            Read the Turkish version →
          </a>
        </p>
      )}

      <BriefingCTA variant="inline" />

      {/* More ideas */}
      {related.length > 0 && (
        <section className="mt-12 border-t border-ink/10 pt-10">
          <p className="text-[10px] uppercase tracking-[0.22em] text-clay mb-4">More ideas</p>
          <ul className="space-y-4">
            {related.map((r) => (
              <li key={r.slug}>
                <Link to="/ideas/$slug" params={{ slug: r.slug }} className="group block">
                  <p className="font-serif text-lg text-ink group-hover:text-clay leading-snug">
                    {r.title}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">{r.subtitle}</p>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}
